import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Plus, ShieldCheck, WarningCircle, CurrencyDollar, ClockCountdown, FileText, Gear, PaperPlaneTilt, Envelope, Eye, XCircle, CaretUp, CaretDown, CaretUpDown, ArrowRight } from "@phosphor-icons/react";
import { StatTile, PillTabs, Panel, Stepper, EmptyState, QueueCard, PageActions, PageSubtitle, StatusPill } from "../../components/lux";
import { Field } from "../../components/booking/pickers";

/** INS-019 — the owner's own names, in two groups. `label` is the short name a
 *  badge or a chip carries; `full` is what he calls it out loud, which is what
 *  a toast has to say so "Stage set to Active — Authorized" reads as his
 *  words rather than the CRM's. */
export const STAGE_META = {
  active_unauthorized: { label: "Not authorized", full: "Active — Not authorized", group: "Active", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  active_authorized: { label: "Authorized", full: "Active — Authorized", group: "Active", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  extension_pending: { label: "Extension pending", full: "Active — Extension pending", group: "Active", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  under_investigation: { label: "Under investigation", full: "Active — Under investigation", group: "Active", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  done_invoice_pending: { label: "Invoice pending", full: "Done — Invoice pending", group: "Done", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  done_issued: { label: "Issued", full: "Done — Issued", group: "Done", cls: "bg-primary/15 text-primary border-primary/30" },
  done_received: { label: "Received", full: "Done — Received", group: "Done", cls: "bg-emerald-600/20 text-emerald-300 border-emerald-500/40" },
  done_incorrect: { label: "Incorrect", full: "Done — Incorrect", group: "Done", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  cancelled: { label: "Cancelled", full: "Cancelled", group: null, cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const STAGE_GROUPS = ["Active", "Done"];
/** The two stages the coverage derives. Everything else is the owner's word. */
export const DERIVED_STAGES = ["active_unauthorized", "active_authorized"];
export const stagesInGroup = (g) => Object.keys(STAGE_META).filter((k) => STAGE_META[k].group === g);
export const money = (n) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(n || 0);

export function StageBadge({ stage }) {
  const m = STAGE_META[stage];
  // A stage this build does not know — a case the migration has not reached —
  // prints its own value rather than borrowing another stage's label and
  // quietly lying about where the claim is.
  if (!m) {
    return (
      <StatusPill plain dot={false} toneClass="bg-secondary/40 text-muted-foreground border-border"
        testid={`stage-badge-${stage || "unknown"}`}>{stage || "—"}</StatusPill>
    );
  }
  return <StatusPill plain dot={false} toneClass={m.cls} title={m.full} testid={`stage-badge-${stage}`}>{m.label}</StatusPill>;
}

/** INS-026 — the six reasons a row needs a human, in the order the SERVER
 *  resolves them. It sends one key per case and this file owns the words; the
 *  order is repeated here only so the column can sort by urgency.
 *
 *  An overdue follow-up is deliberately NOT one of them — the Follow-up column
 *  already says Overdue, and duplicating the commonest reason would make this
 *  column useless. */
export const ATTENTION_META = {
  blocked_draft: { label: "Draft blocked", full: "An email is drafted but cannot be sent until the case is fixed" },
  stalled: { label: "Chase stalled", full: "The reminder cadence stopped after too many unanswered nudges" },
  bill_blocked: { label: "Can't bill", full: "The billing date arrived and this case could not be invoiced" },
  coverage_expired: { label: "Coverage expired", full: "The approved-until date has passed and the claim is still active" },
  car_out_past_approval: { label: "Car out past approval", full: "A car is booked out past the date the carrier approved" },
  invoice_unsent: { label: "Invoice not sent", full: "An invoice was raised and nobody has emailed it" },
};
export const ATTENTION_ORDER = Object.keys(ATTENTION_META);

/** One badge, or nothing. Muted amber and a left border on the row — no shades
 *  of red, because degrees of urgency in colour is how a list stops being read. */
function AttentionCell({ c }) {
  const m = ATTENTION_META[c.attention];
  if (!m) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      title={m.full}
      data-reason={c.attention}
      data-testid={`attention-${c.id}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium whitespace-nowrap bg-amber-500/10 text-amber-300 border-amber-500/25"
    >
      <WarningCircle size={12} className="shrink-0" aria-hidden="true" />{m.label}
    </span>
  );
}

const EMPTY_FORM = {
  client_name: "", client_phone: "", client_email: "", insurance_company: "",
  policy_number: "", claim_number: "", adjuster_name: "", adjuster_phone: "",
  adjuster_email: "", accident_date: "", client_vehicle: "", notes: "",
};

const INTAKE_STEPS = [
  { key: "client", label: "Client" },
  { key: "claim", label: "Insurer & claim" },
  { key: "vehicle", label: "Vehicle & notes" },
];

const OPEN_STAGES = ["active_unauthorized", "active_authorized", "extension_pending", "under_investigation"];
const FU_DUE = "follow_up_due";
/** INS-026 — counts the Attention column, not overdue follow-ups. */
const ATTN = "needs_attention";

/** Armed, with a due date that has arrived. `days_until_follow_up` is computed
 *  server-side by `compute_summary`, so the list agrees with the drafting job. */
const isFollowUpDue = (c) => {
  const fu = c.follow_up || {};
  const d = c.summary?.days_until_follow_up;
  return !!fu.enabled && d !== null && d !== undefined && d <= 0;
};

/** Off · Due <date> · Overdue <date>. The word carries the meaning; the colour
 *  only reinforces it. Stacked on two lines to keep the column narrow. */
function FollowUpCell({ c }) {
  const fu = c.follow_up || {};
  if (!fu.enabled) return <span className="text-muted-foreground">—</span>;
  // Stalled outranks the due date. The cadence has stopped sending on its own,
  // so "Overdue" here would be describing a clock that is no longer running.
  if ((fu.stalled_at || "").trim()) {
    const n = fu.nudge_count || 0;
    return (
      <span className="block whitespace-nowrap" data-testid="fu-stalled">
        <span className="text-amber-400 font-medium">Stalled</span>
        <span className="block text-[11px] tnum text-amber-400/80">{n} unanswered</span>
      </span>
    );
  }
  const due = (fu.next_due_at || "").slice(0, 10);
  if (!due) return <span className="text-muted-foreground">On</span>;
  const overdue = isFollowUpDue(c);
  return (
    <span className="block whitespace-nowrap" data-testid={overdue ? "fu-overdue" : "fu-due"}>
      <span className={overdue ? "text-red-400 font-medium" : "text-muted-foreground"}>{overdue ? "Overdue" : "Due"}</span>
      <span className={`block text-[11px] tnum ${overdue ? "text-red-400/80" : "text-muted-foreground"}`}>{due}</span>
    </span>
  );
}

/** Neediest first: stalled, then overdue, then due, then armed-but-not-due,
 *  then off — and by date inside each state, so the cases that want a human
 *  float to the top. Returned as a tuple the generic comparator walks. */
const followUpKey = (c) => {
  const fu = c.follow_up || {};
  if (!fu.enabled) return [4, 0];
  if ((fu.stalled_at || "").trim()) return [0, -(fu.nudge_count || 0)];
  const d = c.summary?.days_until_follow_up;
  if (d === null || d === undefined) return [3, 0];
  return [d < 0 ? 1 : 2, d];
};

/** Which columns sort, and which direction is most useful on the FIRST click —
 *  names A→Z, dates soonest, money biggest, follow-up neediest. */
const SORTS = {
  /* INS-023: the column is Claim now and it sorts by claim number. The 14 rows
     without one sink (see `sortRows`) and hold a stable order among themselves
     on the case number, which is the only unique handle there is. */
  claim: { first: "asc", get: (c) => c.claim_number || "" },
  coverage_ends: { first: "asc", get: (c) => (c.coverage?.approved_until || "").slice(0, 10) },
  running: { first: "desc", get: (c) => c.summary?.running_total ?? null },
  balance: { first: "desc", get: (c) => c.invoice_balance ?? null },
  follow_up: { first: "asc", get: followUpKey },
  /* INS-026: by the server's priority, then the case number through
     `sortRows` — a row with nothing to answer for sinks in both directions. */
  attention: { first: "asc", get: (c) => (c.attention ? ATTENTION_ORDER.indexOf(c.attention) : null) },
};

const blank = (v) => v === null || v === undefined || v === "";

const compare = (a, b) => {
  if (Array.isArray(a)) {
    for (let i = 0; i < a.length; i += 1) {
      const r = compare(a[i], b[i]);
      if (r) return r;
    }
    return 0;
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

/** Blanks sink to the bottom in BOTH directions — a case with no coverage date
 *  is missing information, not the earliest one. Ties and the sunk blanks fall
 *  through to the case number, so the order never wobbles between renders. */
const sortRows = (rows, key, dir) => {
  const get = SORTS[key]?.get;
  if (!get) return rows;
  const flip = dir === "desc" ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = get(x);
    const b = get(y);
    if (blank(a) || blank(b)) {
      if (!blank(a) || !blank(b)) return blank(a) ? 1 : -1;
    } else {
      const r = flip * compare(a, b);
      if (r) return r;
    }
    return compare(x.case_number || "", y.case_number || "");
  });
};

const SortTH = ({ label, sortKey, activeKey, dir, onSort, className = "" }) => {
  const active = activeKey === sortKey;
  const next = !active ? SORTS[sortKey].first : dir === "asc" ? "descending" : "the default order";
  const Icon = !active ? CaretUpDown : dir === "asc" ? CaretUp : CaretDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={`px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal ${className}`}
    >
      <button
        type="button" onClick={() => onSort(sortKey)}
        /* `uppercase` because a base rule resets text-transform on button, so
           the label would otherwise escape .label-mono on the th. */
        className={`inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded ${active ? "text-foreground" : ""}`}
        data-testid={`sort-${sortKey}`}
      >
        {label}
        <Icon size={12} className={active ? "text-primary" : "opacity-40"} aria-hidden="true" />
        <span className="sr-only">
          {active ? `sorted ${dir === "asc" ? "ascending" : "descending"}, ` : ""}sort by {label} {next === "asc" ? "ascending" : next === "desc" ? "descending" : next}
        </span>
      </button>
    </th>
  );
};

export default function InsuranceList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tableRef = useRef(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  // INS-020: the wizard's one field-level message. Set when Continue is pressed
  // with a blank name, or when Create case sends him back to fix it.
  const [nameError, setNameError] = useState(false);

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ["insurance-cases"],
    queryFn: async () => (await api.get("/insurance")).data,
  });

  const { data: drafts = [] } = useQuery({
    queryKey: ["insurance-reminders"],
    queryFn: async () => (await api.get("/insurance/reminders", { params: { status: "draft" } })).data,
  });
  // Blocked drafts can't be sent by anyone until the case is fixed, so the count
  // belongs on the button — otherwise "Reminders (4)" reads as four sendable.
  const blockedCount = drafts.filter((d) => d.blocked).length;

  // Loaded once on mount: the warning window is a setting, not a constant, and
  // the same cache entry backs the Settings dialog so a save updates the page.
  const { data: insSettings } = useQuery({
    queryKey: ["insurance-settings"],
    queryFn: async () => (await api.get("/insurance/settings")).data,
  });
  const warnDays = insSettings?.auth_reminder_days ?? 5;
  const dayWord = (n) => `${n} day${n === 1 ? "" : "s"}`;

  const createCase = useMutation({
    mutationFn: async () => (await api.post("/insurance", form)).data,
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["insurance-cases"] });
      setOpen(false);
      setForm(EMPTY_FORM);
      setStep(0);
      setNameError(false);
      toast.success(`Case ${c.case_number} created`);
      navigate(`/insurance/${c.id}`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const stages = Object.keys(STAGE_META);
  const counts = stages.reduce((acc, s) => ({ ...acc, [s]: cases.filter((c) => c.stage === s).length }), {});
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const daysLeft = (c) => c.summary?.days_until_coverage_end;
  const openCases = cases.filter((c) => OPEN_STAGES.includes(c.stage));
  const expiring = openCases
    .filter((c) => daysLeft(c) !== null && daysLeft(c) !== undefined && daysLeft(c) <= warnDays)
    .sort((a, b) => daysLeft(a) - daysLeft(b));
  const exposure = openCases.reduce((s, c) => s + (c.summary?.running_total || 0), 0);
  const awaitingInvoice = cases.filter((c) => c.stage === "done_invoice_pending").length;
  const followUpDue = cases.filter((c) => isFollowUpDue(c));
  // INS-026: one ranked signal per case, resolved server-side. This counts the
  // Attention column and nothing else — an overdue follow-up is not in it.
  const needsAttention = cases.filter((c) => !!c.attention);
  // INS-019: the chips are two levels. A GROUP key filters to everything in
  // that group and opens a second row; a STAGE key filters to that one stage
  // and leaves its group lit, so nine flat chips are never on screen at once.
  const groupOf = (s) => STAGE_META[s]?.group || null;
  const openGroup = STAGE_GROUPS.includes(stageFilter) ? stageFilter : groupOf(stageFilter);
  const visible = stageFilter === "all" ? cases
    : stageFilter === FU_DUE ? followUpDue
    : stageFilter === ATTN ? needsAttention
    : STAGE_GROUPS.includes(stageFilter) ? cases.filter((c) => groupOf(c.stage) === stageFilter)
    : cases.filter((c) => c.stage === stageFilter);

  // Sort lives in the URL so a reload — or a shared link — lands on the same
  // view, and so this can move server-side later without changing the UI.
  const sortKey = SORTS[params.get("sort")] ? params.get("sort") : "";
  const sortDir = params.get("dir") === "desc" ? "desc" : "asc";
  const rows = sortKey ? sortRows(visible, sortKey, sortDir) : visible;

  const applySort = (key, dir) => {
    const next = new URLSearchParams(params);
    if (!key) { next.delete("sort"); next.delete("dir"); }
    else { next.set("sort", key); next.set("dir", dir); }
    setParams(next, { replace: true });
  };
  // Three states: the useful direction, its reverse, then back to how the
  // page arrived — so nobody has to reload to undo a sort.
  const onSort = (key) => {
    if (sortKey !== key) return applySort(key, SORTS[key].first);
    if (sortDir === SORTS[key].first) return applySort(key, SORTS[key].first === "asc" ? "desc" : "asc");
    return applySort("");
  };
  const showAllExpiring = () => {
    setStageFilter("all");
    applySort("coverage_ends", "asc");
    requestAnimationFrame(() => {
      // The topbar is `md:sticky`, so it only covers the table from md up —
      // measure it rather than hard-code a height that the two-row header,
      // the mobile search box or a longer page title would all invalidate.
      const bar = document.querySelector('[data-testid="topbar"]');
      const sticky = bar && getComputedStyle(bar).position === "sticky";
      if (tableRef.current) {
        tableRef.current.style.scrollMarginTop = sticky ? `${Math.ceil(bar.getBoundingClientRect().height)}px` : "0px";
      }
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const tabs = [
    { key: "all", label: "All", count: cases.length, testid: "stage-filter-all" },
    { key: ATTN, label: "Needs attention", count: needsAttention.length, testid: "stage-filter-attention" },
    { key: FU_DUE, label: "Follow-up due", count: followUpDue.length, testid: "stage-filter-follow-up-due" },
    ...STAGE_GROUPS.map((g) => ({
      key: g, label: g, testid: `stage-filter-group-${g.toLowerCase()}`,
      count: cases.filter((c) => groupOf(c.stage) === g).length,
    })),
    // Cancelled belongs to neither group: a claim that never happened is not a
    // live case and it is not a finished one either.
    ...(counts.cancelled > 0 || stageFilter === "cancelled"
      ? [{ key: "cancelled", label: STAGE_META.cancelled.label, count: counts.cancelled, testid: "stage-filter-cancelled" }]
      : []),
  ];
  const subTabs = openGroup
    ? stagesInGroup(openGroup)
      .filter((s) => counts[s] > 0 || s === stageFilter)
      .map((s) => ({ key: s, label: STAGE_META[s].label, count: counts[s], testid: `stage-filter-${s}` }))
    : [];

  return (
    <div className="space-y-6" data-testid="insurance-list-page">
      <PageSubtitle>
        {openCases.length} live case{openCases.length === 1 ? "" : "s"} · {expiring.length} approval{expiring.length === 1 ? "" : "s"} ending within {dayWord(warnDays)}
      </PageSubtitle>
      <PageActions>
        <Button variant="outline" onClick={() => setRemindersOpen(true)} data-testid="insurance-reminders-button">
          <Envelope size={16} className="mr-1.5" /> Reminders ({drafts.length}{blockedCount ? ` · ${blockedCount} blocked` : ""})
        </Button>
        <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="insurance-settings-button">
          <Gear size={16} className="mr-1.5" /> Settings
        </Button>
        <Button onClick={() => { setStep(0); setNameError(false); setOpen(true); }} data-testid="new-insurance-case-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus size={16} className="mr-1.5" /> New Case
        </Button>
      </PageActions>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <StatTile label="Live Cases" value={openCases.length} hint="Every Active stage" icon={ShieldCheck} tone="green" testid="ins-kpi-live" />
        <StatTile label="Approvals Ending" value={expiring.length} hint={`Within the next ${dayWord(warnDays)}`} icon={ClockCountdown} tone="red" testid="ins-kpi-expiring" />
        <StatTile label="Running Exposure" value={money(exposure)} gold hint="Billable to insurers on live cases" icon={CurrencyDollar} tone="gold" testid="ins-kpi-exposure" />
        <StatTile label="Awaiting Invoice" value={awaitingInvoice} hint="Done, invoice not raised yet" icon={FileText} tone="amber" testid="ins-kpi-awaiting" />
      </div>

      {expiring.length > 0 && (
        <div>
          <div className="label-mono mb-3">Coverage running out</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {expiring.slice(0, 6).map((c) => {
              const dl = daysLeft(c);
              return (
                <QueueCard
                  key={c.id}
                  testid={`expiring-card-${c.id}`}
                  title={c.client_name || c.case_number}
                  meta={`${c.case_number}${c.assigned_vehicle_label ? ` · ${c.assigned_vehicle_label}` : ""}`}
                  figureLabel={dl < 0 ? "Expired" : "Approved until"}
                  figure={dl < 0 ? `${Math.abs(dl)}d overdue` : dl === 0 ? "Today" : `${dl} days`}
                  urgency={dl < 0 ? "red" : dl <= 2 ? "amber" : "green"}
                  badges={<StageBadge stage={c.stage} />}
                  action={
                    <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate(`/insurance/${c.id}`)}>
                      Open case
                    </Button>
                  }
                />
              );
            })}
            {expiring.length > 6 && (
              <button
                onClick={showAllExpiring}
                className="flex flex-col items-start justify-center gap-1.5 rounded-lg border border-dashed border-border px-5 py-6 text-left transition-colors hover:border-primary/40 hover:bg-white/[0.02]"
                data-testid="expiring-more-button"
              >
                <span className="font-heading text-xl">+{expiring.length - 6} more</span>
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  See every approval by coverage date <ArrowRight size={14} aria-hidden="true" />
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2.5">
        <PillTabs tabs={tabs} value={openGroup || stageFilter} onChange={setStageFilter} testid="stage-tabs" />
        {subTabs.length > 0 && (
          <PillTabs tabs={subTabs} value={stageFilter} onChange={setStageFilter} testid="stage-subtabs" size="sm" />
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-card border border-border animate-pulse" />)}</div>
      ) : visible.length === 0 ? (
        <div className="border border-border bg-card">
          <EmptyState
            icon={ShieldCheck}
            title={stageFilter === FU_DUE ? "Nothing due for follow-up"
              : stageFilter === ATTN ? "Nothing needs attention"
              : `No cases${stageFilter !== "all" ? " in this stage" : ""} yet`}
            hint={stageFilter === FU_DUE
              ? "A case shows up here once Chase this case is on and its next due date has arrived."
              : stageFilter === ATTN
              ? "A blocked draft, a stalled chase, a case that could not be billed, an expired approval, a car out past its approval and an invoice nobody has sent all land here."
              : "Insurance replacement cases track adjuster approvals, daily rates and the running total against the max."}
            action={stageFilter === FU_DUE || stageFilter === ATTN ? undefined : <Button variant="outline" onClick={() => setOpen(true)}>Start the first case</Button>}
          />
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto" data-testid="insurance-table-wrap" ref={tableRef}>
          <table className="w-full text-[13px] min-[1650px]:text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <SortTH label="Claim" sortKey="claim" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                <th scope="col" className="px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal">Client</th>
                <th scope="col" className="px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal">Insurer</th>
                <th scope="col" className="px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal">Vehicle</th>
                <th scope="col" className="px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal text-right">Daily rate</th>
                <SortTH label="Coverage ends" sortKey="coverage_ends" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                <SortTH label="Follow-up" sortKey="follow_up" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                <SortTH label="Attention" sortKey="attention" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                <SortTH label="Running / Max" sortKey="running" activeKey={sortKey} dir={sortDir} onSort={onSort} className="text-right" />
                <SortTH label="Balance" sortKey="balance" activeKey={sortKey} dir={sortDir} onSort={onSort} className="text-right" />
                <th scope="col" className="px-2.5 min-[1650px]:px-4 py-3.5 label-mono font-normal text-right">Stage</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const s = c.summary || {};
                const dl = s.days_until_coverage_end;
                const endingSoon = dl !== null && dl !== undefined && dl <= warnDays && OPEN_STAGES.includes(c.stage);
                const bal = c.invoice_balance;
                return (
                  <tr key={c.id} onClick={() => navigate(`/insurance/${c.id}`)} data-testid={`insurance-row-${c.id}`}
                    /* INS-026: a left border, never a filled background —
                       fourteen red rows in a list of twenty means nothing. */
                    className={`border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] cursor-pointer transition-colors ${c.attention ? "border-l-2 border-l-amber-500/70" : ""}`}>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5">
                      {/* A real link, so the row is reachable and openable by keyboard.
                          INS-023: claim number on top, case number beneath — the
                          aria-label carries the case number because that is the
                          one handle that cannot be blank or shared. */}
                      <Link to={`/insurance/${c.id}`} onClick={(e) => e.stopPropagation()}
                        aria-label={`Open case ${c.case_number}`}
                        title={c.claim_number ? `Claim ${c.claim_number}` : "No claim number on this case"}
                        className="block font-heading text-[15px] whitespace-nowrap truncate max-w-[120px] min-[1650px]:max-w-[220px] hover:text-primary transition-colors" data-testid={`insurance-open-${c.id}`}>
                        {c.claim_number || <span className="text-muted-foreground">No claim #</span>}
                      </Link>
                      <span className="block text-[11px] text-muted-foreground truncate max-w-[84px] min-[1650px]:max-w-[160px]">
                        {c.case_number}
                      </span>
                    </td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5"><span className="block truncate max-w-[84px] min-[1650px]:max-w-[160px]" title={c.client_name || ""}>{c.client_name || "—"}</span></td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5"><span className="block truncate max-w-[84px] min-[1650px]:max-w-[160px] text-muted-foreground" title={c.insurance_company || ""}>{c.insurance_company || "—"}</span></td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5">
                      <span className="block truncate max-w-[84px] min-[1650px]:max-w-[160px]" title={c.assigned_vehicle_label || ""}>
                        {c.assigned_vehicle_label || <span className="text-muted-foreground">Unassigned</span>}
                      </span>
                    </td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5 text-right money whitespace-nowrap">{s.daily_rate_total ? money(s.daily_rate_total) : "—"}</td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5">
                      <span className={endingSoon ? "text-amber-400 flex items-center gap-1.5 whitespace-nowrap" : "text-muted-foreground tnum whitespace-nowrap"}>
                        {endingSoon && <WarningCircle size={14} className="shrink-0" />}
                        {(c.coverage?.approved_until || "").slice(0, 10) || "—"}
                      </span>
                    </td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5"><FollowUpCell c={c} /></td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5"><AttentionCell c={c} /></td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5 text-right">
                      <span className="money block whitespace-nowrap">{s.running_total ? money(s.running_total) : "—"}</span>
                    </td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5 text-right whitespace-nowrap" data-testid={`insurance-balance-${c.id}`}>
                      {bal === null || bal === undefined ? "" : bal <= 0
                        ? <span className="label-mono">Paid</span>
                        : <span className="money">{money(bal)}</span>}
                    </td>
                    <td className="px-2.5 min-[1650px]:px-4 py-3.5 text-right"><StageBadge stage={c.stage} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Guided intake */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-case-dialog">
          <DialogHeader>
            <DialogTitle>New insurance replacement case</DialogTitle>
            <DialogDescription>Three steps: the client, the insurer and claim, then the vehicle. You can change anything afterwards on the case page.</DialogDescription>
          </DialogHeader>
          <Stepper steps={INTAKE_STEPS} current={step} onStep={setStep} testid="intake-stepper" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
            {step === 0 && (
              <>
                {/* INS-020: a field-level message, not a toast that refuses the
                    step. Continue advances; Create case is the gate. */}
                <Field label="Client name" required error={nameError && !form.client_name.trim() ? "A client name is needed — a case without one can't be worked." : undefined}>
                  <Input value={form.client_name} onChange={set("client_name")} data-testid="case-client-name" />
                </Field>
                <Field label="Client phone"><Input value={form.client_phone} onChange={set("client_phone")} data-testid="case-client-phone" /></Field>
                <Field label="Client email" className="sm:col-span-2"><Input value={form.client_email} onChange={set("client_email")} /></Field>
                <Field label="Accident date"><Input type="date" value={form.accident_date} onChange={set("accident_date")} /></Field>
              </>
            )}
            {step === 1 && (
              <>
                <Field label="Insurance company"><Input value={form.insurance_company} onChange={set("insurance_company")} data-testid="case-insurance-company" /></Field>
                <Field label="Claim number"><Input value={form.claim_number} onChange={set("claim_number")} data-testid="case-claim-number" /></Field>
                <Field label="Policy number"><Input value={form.policy_number} onChange={set("policy_number")} /></Field>
                <Field label="Adjuster name"><Input value={form.adjuster_name} onChange={set("adjuster_name")} /></Field>
                <Field label="Adjuster phone"><Input value={form.adjuster_phone} onChange={set("adjuster_phone")} /></Field>
                <Field label="Adjuster email"><Input value={form.adjuster_email} onChange={set("adjuster_email")} /></Field>
              </>
            )}
            {step === 2 && (
              <>
                <Field label="Client's own vehicle" className="sm:col-span-2"><Input placeholder="e.g. 2021 Honda Civic — ABCD 123" value={form.client_vehicle} onChange={set("client_vehicle")} /></Field>
                <Field label="Notes" className="sm:col-span-2"><Input value={form.notes} onChange={set("notes")} /></Field>
                <div className="sm:col-span-2 border border-border rounded-2xl p-4">
                  <div className="label-mono mb-2">Ready to open</div>
                  <div className="text-sm">
                    {form.client_name || "—"}{form.insurance_company ? ` · ${form.insurance_company}` : ""}{form.claim_number ? ` · claim ${form.claim_number}` : ""}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">You'll assign the vehicle and daily rate on the case page next.</p>
                </div>
              </>
            )}
          </div>
          <div className="flex justify-between gap-2 mt-6">
            <Button variant="ghost" onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))} data-testid="intake-back">
              {step === 0 ? "Cancel" : "Back"}
            </Button>
            {step < INTAKE_STEPS.length - 1 ? (
              <Button
                onClick={() => { setNameError(step === 0 && !form.client_name.trim()); setStep(step + 1); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="intake-next"
              >
                Continue
              </Button>
            ) : (
              <Button onClick={() => {
                if (form.client_name.trim()) { createCase.mutate(); return; }
                // The gate, and it says where to fix it rather than just refusing.
                setNameError(true);
                setStep(0);
              }}
                disabled={createCase.isPending} data-testid="create-case-submit" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {createCase.isPending ? "Creating…" : "Create case"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <InsuranceSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <RemindersDialog open={remindersOpen} onOpenChange={setRemindersOpen} drafts={drafts} />
    </div>
  );
}

function InsuranceSettingsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: s } = useQuery({
    queryKey: ["insurance-settings"],
    queryFn: async () => (await api.get("/insurance/settings")).data,
    enabled: open,
  });
  const save = useMutation({
    mutationFn: async (patch) => (await api.put("/insurance/settings", patch)).data,
    onSuccess: (d) => { qc.setQueryData(["insurance-settings"], d); toast.success("Settings saved"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const sendNow = useMutation({
    mutationFn: async () => (await api.post("/insurance/settings/send-digest-now")).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["insurance-settings"] });
      if (r.sent) toast.success(`Digest sent to ${r.to} — ${r.count} case(s)`);
      else toast.error(REASONS[r.reason] || `Not sent (${r.reason})`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="insurance-settings-dialog">
        <DialogHeader>
          <DialogTitle>Insurance settings</DialogTitle>
          <DialogDescription>Grouped by WHO reads the email, not by which feature drafts it.</DialogDescription>
        </DialogHeader>
        <SettingsBody s={s} save={save} sendNow={sendNow} />
      </DialogContent>
    </Dialog>
  );
}

/** 12-hour label for a 24-hour integer. The stored value is always the integer;
 *  only the label is human. */
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
/** 6 AM – 10 PM, plus whatever is currently stored if it sits outside that —
 *  a select that silently drops the saved hour would change behaviour on open. */
const hourOptions = (current) => {
  const base = Array.from({ length: 17 }, (_, i) => i + 6);
  return base.includes(current) ? base : [...base, current].sort((a, b) => a - b);
};

const SLOTS = "{adjuster_name} {insurance_company} {claim_number} {policy_number} {client_name} {client_vehicle} {assigned_vehicle} {rental_start} {rental_end} {last_auth_date} {daily_rate} {addons} {invoice_number} {invoice_total} {invoice_balance} {invoice_date} {invoice_link} {days_outstanding} {sender_name} {sender_phone} {business_name} {business_address} {cheque_payable_to} {etransfer_email}";

/** INS-024 — the one fixed PDF that rides along with every invoice email.
 *  Its own endpoints, because a file is not a settings field: the upload date
 *  is read back from the stored file rather than kept as a third setting, so
 *  Remove clears exactly the two keys that Upload wrote. */
const DepositFormField = () => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [removing, setRemoving] = useState(false);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: form } = useQuery({
    queryKey: ["insurance-deposit-form"],
    queryFn: async () => (await api.get("/insurance/settings/deposit-form/info")).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["insurance-deposit-form"] });
    qc.invalidateQueries({ queryKey: ["insurance-settings"] });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // so picking the same file twice still fires a change
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/insurance/settings/deposit-form", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
      toast.success(`${data.filename} will be attached to invoice emails`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Shown in-app rather than in a new tab: a fetched PDF only exists as a blob:
  // URL, and Chromium will not navigate a freshly opened tab to one — it lands
  // on about:blank with no error, which is exactly what the first cut did. An
  // embed renders it anywhere, and the download link covers the rest.
  const view = async () => {
    try {
      const { data } = await api.get("/insurance/settings/deposit-form", { responseType: "blob" });
      setPreview(URL.createObjectURL(data));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
  };

  const remove = async () => {
    setRemoving(false);
    try {
      await api.delete("/insurance/settings/deposit-form");
      refresh();
      toast.success("Direct deposit form removed — invoice emails go out without it");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-2.5" data-testid="deposit-form-card">
      <div className="label-mono">Direct deposit form</div>
      <p className="text-xs text-muted-foreground -mt-1">Attached to every invoice email. PDF only.</p>
      <input ref={fileRef} type="file" accept="application/pdf" className="sr-only"
        onChange={upload} aria-hidden="true" tabIndex={-1} data-testid="deposit-form-input" />
      {form?.file_id ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm flex items-center gap-1.5 min-w-0" data-testid="deposit-form-name">
            <FileText size={14} className="text-muted-foreground shrink-0" />
            <span className="truncate">{form.filename}</span>
          </span>
          <span className="text-xs text-muted-foreground" data-testid="deposit-form-date">
            uploaded {(form.uploaded_at || "").slice(0, 10) || "—"}
          </span>
          <Button size="sm" variant="outline" onClick={view} data-testid="deposit-form-view">View</Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => fileRef.current?.click()} data-testid="deposit-form-replace">
            {busy ? "Uploading…" : "Replace"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRemoving(true)} data-testid="deposit-form-remove">
            Remove
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" disabled={busy}
          onClick={() => fileRef.current?.click()} data-testid="deposit-form-upload">
          {busy ? "Uploading…" : "Upload a PDF"}
        </Button>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-3xl" data-testid="deposit-form-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText size={15} className="text-muted-foreground" />
              {form?.filename || "Direct deposit form"}
            </DialogTitle>
            <DialogDescription>
              This is the file every invoice email carries.{" "}
              <a href={preview} download={form?.filename || "direct-deposit-form.pdf"}
                className="underline hover:text-foreground" data-testid="deposit-form-download">Download it</a>{" "}
              if your browser will not show it here.
            </DialogDescription>
          </DialogHeader>
          <embed src={preview} type="application/pdf"
            className="w-full h-[68vh] rounded-md border border-border/60 bg-white" />
        </DialogContent>
      </Dialog>

      <AlertDialog open={removing} onOpenChange={setRemoving}>        <AlertDialogContent data-testid="deposit-form-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove the direct deposit form?</AlertDialogTitle>
            <AlertDialogDescription>
              Invoice emails will go out without it until you upload one again. The file itself stays in
              storage, so nothing is lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deposit-form-remove-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="deposit-form-remove-confirm">Remove it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function SettingsBody({ s, save, sendNow }) {
  const [local, setLocal] = useState({});
  const [resetting, setResetting] = useState(null);
  const [email, setEmail] = useState("");
  const { data: defaults } = useQuery({
    queryKey: ["insurance-template-defaults"],
    queryFn: async () => (await api.get("/insurance/settings/template-defaults")).data,
  });
  useEffect(() => {
    if (!s) return;
    setEmail(s.auth_reminder_email || "");
    setLocal({
      sender_display_name: s.sender_display_name || "",
      reply_to_email: s.reply_to_email || "", bcc_email: s.bcc_email || "",
      sender_name: s.sender_name || "", sender_phone: s.sender_phone || "",
      business_name: s.business_name || "", business_address: s.business_address || "",
      cheque_payable_to: s.cheque_payable_to || "", etransfer_email: s.etransfer_email || "",
      auth_request_subject: s.auth_request_subject || "", auth_request_body: s.auth_request_body || "",
      template_a_subject: s.template_a_subject || "", template_a_body: s.template_a_body || "",
      template_b_subject: s.template_b_subject || "", template_b_body: s.template_b_body || "",
      invoice_email_subject: s.invoice_email_subject || "", invoice_email_body: s.invoice_email_body || "",
      invoice_ready_subject: s.invoice_ready_subject || "", invoice_ready_body: s.invoice_ready_body || "",
      extension_request_subject: s.extension_request_subject || "", extension_request_body: s.extension_request_body || "",
    });
  }, [s]);
  // Blur fires whether or not anything changed, so only save real edits.
  const commit = (key) => { if (local[key] !== (s?.[key] || "")) save.mutate({ [key]: local[key] }); };
  const bind = (key, testid) => ({
    value: local[key] ?? "",
    onChange: (e) => setLocal({ ...local, [key]: e.target.value }),
    onBlur: () => commit(key),
    "data-testid": testid,
  });
  const num = (key, value, lo, hi) => {
    const n = parseInt(value, 10);
    if (n >= lo && n <= hi) save.mutate({ [key]: n });
  };
  const RESET_LABELS = {
    auth_request_subject: "Authorization request subject",
    auth_request_body: "Authorization request body",
    template_a_subject: "Script A subject",
    template_a_body: "Script A body",
    template_b_subject: "Script B subject",
    template_b_body: "Script B body",
    invoice_email_subject: "Invoice email subject",
    invoice_email_body: "Invoice email cover note",
    invoice_ready_subject: "Ready-to-send subject",
    invoice_ready_body: "Ready-to-send note",
    extension_request_subject: "Extension request subject",
    extension_request_body: "Extension request body",
  };
  const ResetLink = ({ field }) => (
    <button
      type="button"
      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-40"
      disabled={!defaults}
      title={defaults ? "Replace this one template with the original wording" : "Loading the original wording…"}
      onClick={() => setResetting(field)}
      data-testid={`reminders-reset-${field}`}
    >
      Reset to default
    </button>
  );

  return (
    <Tabs defaultValue="reminders" className="pt-1">
      <TabsList>
        <TabsTrigger value="reminders" data-testid="settings-tab-reminders">Emails to adjusters</TabsTrigger>
        <TabsTrigger value="digest" data-testid="settings-tab-digest">Emails to us</TabsTrigger>
      </TabsList>

      <TabsContent value="reminders" className="space-y-5 pt-4">
      <p className="text-xs text-muted-foreground" data-testid="settings-outbound-note">
        Every email on this tab goes to a carrier, in your name.
      </p>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm">Reminder drafting</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Each morning, every case marked Chase this case with an unpaid invoice gets a draft in the Reminders queue.
          </div>
        </div>
        <Switch
          checked={!!s?.payment_reminder_enabled}
          onCheckedChange={(v) => save.mutate({ payment_reminder_enabled: !!v })}
          disabled={!s || save.isPending}
          aria-label="Reminder drafting"
          data-testid="reminders-enabled-switch"
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5">
        <div>
          <div className="text-sm text-amber-200">Send automatically, without review</div>
          <div className="text-xs text-amber-200/70 mt-0.5">
            Leave this off. With it on, emails go to adjusters with nobody reading them first — including an adjuster who replied to you an hour ago.
          </div>
        </div>
        <Switch
          checked={!!s?.auto_send}
          onCheckedChange={(v) => save.mutate({ auto_send: !!v })}
          disabled={!s || save.isPending}
          aria-label="Send automatically, without review"
          data-testid="reminders-autosend-switch"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Auto-send at most (per day)"
          hint="A breaker for anomalies, not a ration. Anything over the cap stays in the queue for you to send.">
          <Input
            type="number" min={1} max={500}
            defaultValue={s?.auto_send_daily_cap ?? 50}
            key={`cap-${s?.auto_send_daily_cap}`}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              if (!Number.isNaN(raw)) save.mutate({ auto_send_daily_cap: Math.min(500, Math.max(1, raw)) });
            }}
            data-testid="reminders-daily-cap-input"
          />
        </Field>
        <Field label="Stop after this many unanswered reminders"
          hint="The case is marked stalled: drafts keep coming for you to read, but nothing goes out automatically.">
          <Input
            type="number" min={1} max={50}
            defaultValue={s?.max_unanswered_nudges ?? 10}
            key={`mun-${s?.max_unanswered_nudges}`}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              if (!Number.isNaN(raw)) save.mutate({ max_unanswered_nudges: Math.min(50, Math.max(1, raw)) });
            }}
            data-testid="reminders-max-nudges-input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Emails appear from" className="sm:col-span-2"
          hint="The name above the message in the adjuster's inbox. Without it, Gmail invents one from the mailbox address.">
          <Input placeholder="01 Motors GTA" maxLength={64} {...bind("sender_display_name", "reminders-display-name-input")} />
        </Field>
        <Field label="Reply-To"><Input type="email" placeholder="ray@01motors.ca" {...bind("reply_to_email", "reminders-replyto-input")} /></Field>
        <Field label="BCC a copy to" className="sm:col-span-2"
          hint="Several addresses can be separated by commas. Every reminder and invoice email is copied to all of them.">
          <Input type="text" placeholder="ray@01motors.ca, accounts@01motors.ca" {...bind("bcc_email", "reminders-bcc-input")} />
        </Field>
        <Field label="Signs off as"><Input placeholder="Ray" {...bind("sender_name", "reminders-sender-name-input")} /></Field>
        <Field label="Contact phone in the email"><Input placeholder="(416) 555-0134" {...bind("sender_phone", "reminders-sender-phone-input")} /></Field>
      </div>
      {/* INS-022 — the business's own details, in one place. They were hardcoded
          in three templates, which is how "01 Motors" ended up signing emails
          from 01 Motors GTA Inc. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Business name" className="sm:col-span-2"
          hint="Signs every outbound email as {business_name}, and names the company in the authorization request.">
          <Input placeholder="01 Motors GTA Inc." {...bind("business_name", "reminders-business-name-input")} />
        </Field>
        <Field label="Business address" className="sm:col-span-2"
          hint="Where a cheque is mailed. Appears in the invoice email only.">
          <Input placeholder="1652 Bayly Street Unit B, Pickering, ON L1W 1L9" {...bind("business_address", "reminders-business-address-input")} />
        </Field>
        <Field label="Cheques payable to" hint="The legal name on the cheque, which is not the trading name.">
          <Input placeholder="1001002728 Ontario Inc." {...bind("cheque_payable_to", "reminders-cheque-payable-input")} />
        </Field>
        <Field label="E-transfer address">
          <Input type="email" placeholder="01motorsgta@gmail.com" {...bind("etransfer_email", "reminders-etransfer-input")} />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="After the adjuster confirms, chase again every (days)">
          <Input
            type="number" min={1} max={30}
            defaultValue={s?.confirmed_interval_days ?? 7}
            key={`ci-${s?.confirmed_interval_days}`}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              // Clamp rather than ignore: typing 99 should save 30, not silently
              // do nothing and leave the old value in place.
              if (!Number.isNaN(raw)) save.mutate({ confirmed_interval_days: Math.min(30, Math.max(1, raw)) });
            }}
            data-testid="reminders-interval-input"
          />
        </Field>
        <Field label="Invoice due after (days)">
          <Input
            type="number" min={1} max={90}
            defaultValue={s?.invoice_due_days ?? 30}
            key={`idd-${s?.invoice_due_days}`}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              if (!Number.isNaN(raw)) save.mutate({ invoice_due_days: Math.min(90, Math.max(1, raw)) });
            }}
            data-testid="reminders-invoice-due-days-input"
          />
        </Field>
      </div>

      <div className="pt-1 border-t border-border/60">
        <div className="label-mono mb-2">Available slots</div>
        <p className="text-xs text-muted-foreground font-mono leading-relaxed" data-testid="reminders-slot-list">{SLOTS}</p>
        <p className="text-xs text-muted-foreground mt-1.5">Anything in braces that isn't on this list is left in the email exactly as typed, so a typo shows up in the draft rather than breaking the run.</p>
      </div>

      <div className="space-y-3">
        <div className="label-mono">Authorization request — "here are our rates", sent by hand at intake</div>
        <p className="text-xs text-muted-foreground -mt-1.5">
          The email that starts a claim. {"{daily_rate}"} and {"{addons}"} are the rates on the case as it
          stands, so this one is worth sending after the rate and the add-ons are set.
        </p>
        <Field label="Subject">
          <Input {...bind("auth_request_subject", "reminders-auth-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="auth-request-body" className="label-mono">Body</Label>
            <ResetLink field="auth_request_body" />
          </div>
          <Textarea id="auth-request-body" rows={13} className="font-mono text-xs" {...bind("auth_request_body", "reminders-auth-body")} />
        </div>
      </div>
      <div className="space-y-3">
        <div className="label-mono">Script A — while unpaid and the adjuster hasn't confirmed</div>
        <Field label="Subject">
          <Input {...bind("template_a_subject", "reminders-template-a-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="template-a-body" className="label-mono">Body</Label>
            <ResetLink field="template_a_body" />
          </div>
          <Textarea id="template-a-body" rows={12} className="font-mono text-xs" {...bind("template_a_body", "reminders-template-a-body")} />
        </div>
      </div>
      <div className="space-y-3">
        <div className="label-mono">Script B — weekly after the adjuster confirms, while still unpaid</div>
        <Field label="Subject">
          <Input {...bind("template_b_subject", "reminders-template-b-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="template-b-body" className="label-mono">Body</Label>
            <ResetLink field="template_b_body" />
          </div>
          <Textarea id="template-b-body" rows={11} className="font-mono text-xs" {...bind("template_b_body", "reminders-template-b-body")} />
        </div>
      </div>
      <div className="space-y-3">
        <div className="label-mono">Invoice email — the cover note when you send an invoice from a case</div>
        <p className="text-xs text-muted-foreground -mt-1.5">
          The invoice itself is included below this note, and {"{invoice_link}"} is the page the adjuster can print from.
        </p>
        <Field label="Subject">
          <Input {...bind("invoice_email_subject", "reminders-invoice-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="invoice-email-body" className="label-mono">Cover note</Label>
            <ResetLink field="invoice_email_body" />
          </div>
          <Textarea id="invoice-email-body" rows={11} className="font-mono text-xs" {...bind("invoice_email_body", "reminders-invoice-body")} />
        </div>
        <DepositFormField />
      </div>
      <div className="space-y-3">
        <div className="label-mono">Extension request — "are you able to extend?", sent by hand from a case</div>
        <p className="text-xs text-muted-foreground -mt-1.5">
          Drafted only when you press Request extension on a case. Nothing sends it automatically, and there is no
          invoice yet, so the {"{invoice_…}"} slots are blank here.
        </p>
        <Field label="Subject">
          <Input {...bind("extension_request_subject", "reminders-extension-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="extension-request-body" className="label-mono">Body</Label>
            <ResetLink field="extension_request_body" />
          </div>
          <Textarea id="extension-request-body" rows={11} className="font-mono text-xs" {...bind("extension_request_body", "reminders-extension-body")} />
        </div>
        {/* INS-025: the one thing on this tab that can draft on its own. It sits
            under the wording it sends, because that is what it sends. */}
        <div className="rounded-lg border border-border/60 p-3 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm">Ask automatically while a car is out past its approval</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Drafts this request each morning for any open case whose approved-until date has passed while the
                car is still out. It stops on its own when the adjuster gives you a new date. Nothing sends itself.
              </div>
            </div>
            <Switch
              checked={!!s?.extension_chase_enabled}
              onCheckedChange={(v) => save.mutate({ extension_chase_enabled: !!v })}
              disabled={!s || save.isPending}
              aria-label="Ask automatically while a car is out past its approval"
              data-testid="extension-chase-switch"
            />
          </div>
          {!!s?.extension_chase_enabled && (
            <Field label="Ask again every (days)">
              <Input
                type="number" min={1} max={30}
                defaultValue={s?.extension_chase_interval_days ?? 1}
                key={`ext-${s?.extension_chase_interval_days}`}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v !== s?.extension_chase_interval_days) save.mutate({ extension_chase_interval_days: v });
                }}
                className="w-24"
                data-testid="extension-chase-interval-input"
              />
            </Field>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="reminders-last-run">
        {s?.last_reminder_run_date ? `Last drafted ${s.last_reminder_run_date}` : "Hasn't drafted yet"}
      </p>
      </TabsContent>

      <TabsContent value="digest" className="space-y-5 pt-4">
      <p className="text-xs text-muted-foreground" data-testid="settings-internal-note">
        Every email on this tab comes to us. No carrier or client ever sees one.
      </p>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm">Daily auth-date digest</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            One email a day listing approvals that are expiring or expired, plus what the morning's run drafted.
          </div>
        </div>
        <Switch
          checked={!!s?.auth_reminder_enabled}
          onCheckedChange={(v) => save.mutate({ auth_reminder_enabled: !!v })}
          disabled={!s || save.isPending}
          aria-label="Daily auth-date digest"
          data-testid="digest-enabled-switch"
        />
      </div>
      <Field label="Send to">
        <Input
          type="email" placeholder="ops@yourcompany.com" value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email !== (s?.auth_reminder_email || "") && save.mutate({ auth_reminder_email: email })}
          data-testid="digest-email-input"
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Warn this many days ahead">
          <Input
            type="number" min={1} max={30} defaultValue={s?.auth_reminder_days ?? 5}
            key={`d-${s?.auth_reminder_days}`}
            onChange={(e) => num("auth_reminder_days", e.target.value, 1, 30)}
            data-testid="digest-days-input"
          />
        </Field>
        <Field label="Send at hour (Toronto)" hint="Reminders are drafted on the hour; the digest follows at half past, so it reports that morning's run.">
          <Select
            value={String(s?.auth_reminder_hour ?? 8)}
            onValueChange={(v) => save.mutate({ auth_reminder_hour: parseInt(v, 10) })}
            disabled={!s || save.isPending}
          >
            <SelectTrigger data-testid="digest-hour-select"><SelectValue placeholder="Pick an hour" /></SelectTrigger>
            <SelectContent>
              {hourOptions(s?.auth_reminder_hour ?? 8).map((h) => (
                <SelectItem key={h} value={String(h)} data-testid={`digest-hour-option-${h}`}>{hourLabel(h)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1 border-t border-border/60">
        <p className="text-xs text-muted-foreground" data-testid="digest-last-sent">
          {s?.last_digest_date ? `Last sent ${s.last_digest_date}` : "Not sent yet"}
          <span className="block mt-0.5">Changing the hour takes effect after the next restart.</span>
        </p>
        <Button
          variant="outline" size="sm" onClick={() => sendNow.mutate()}
          disabled={sendNow.isPending} data-testid="digest-send-now-button"
        >
          <PaperPlaneTilt size={14} className="mr-1.5" />
          {sendNow.isPending ? "Sending…" : "Send now"}
        </Button>
      </div>

      {/* INS-022: the only template on this tab, because it is the only email
          the CRM sends to our own inbox. It sat between two carrier templates
          on the other tab, which is how it got read as one. */}
      <div className="space-y-3 pt-1 border-t border-border/60">
        <div className="label-mono">Ready to send — the note when a bill-on date drafts an invoice</div>
        <p className="text-xs text-muted-foreground -mt-1.5">
          Goes to the address above — one email per morning however many invoices were drafted. This one is
          about several cases at once, so the only slots are {"{count}"} and{" "}
          {"{invoice_list}"}, which becomes the table of what's waiting.
        </p>
        <Field label="Subject">
          <Input {...bind("invoice_ready_subject", "reminders-ready-subject")} />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label htmlFor="invoice-ready-body" className="label-mono">Note</Label>
            <ResetLink field="invoice_ready_body" />
          </div>
          <Textarea id="invoice-ready-body" rows={9} className="font-mono text-xs" {...bind("invoice_ready_body", "reminders-ready-body")} />
        </div>
      </div>
      </TabsContent>

      <AlertDialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <AlertDialogContent data-testid="reminders-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset {RESET_LABELS[resetting] || "this template"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current wording for this one template will be replaced with the original. Nothing else changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reminders-reset-cancel">Keep mine</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const value = defaults?.[resetting];
                if (value !== undefined) {
                  setLocal((o) => ({ ...o, [resetting]: value }));
                  save.mutate({ [resetting]: value });
                }
                setResetting(null);
              }}
              data-testid="reminders-reset-confirm"
            >
              Reset it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}

function RemindersDialog({ open, onOpenChange, drafts }) {
  const qc = useQueryClient();
  const [previewId, setPreviewId] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["insurance-reminders"] });

  const { data: preview } = useQuery({
    queryKey: ["insurance-reminder", previewId],
    queryFn: async () => (await api.get(`/insurance/reminders/${previewId}`)).data,
    enabled: !!previewId,
  });
  const send = useMutation({
    mutationFn: async (id) => (await api.post(`/insurance/reminders/${id}/send`)).data,
    onSuccess: (r) => {
      refresh();
      if (r.sent) toast.success("Reminder sent");
      else toast.error(r.error || "The email could not be sent");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const skip = useMutation({
    mutationFn: async (id) => (await api.post(`/insurance/reminders/${id}/skip`)).data,
    onSuccess: () => { refresh(); toast.success("Skipped — it'll come back on the next due date"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const sendAll = useMutation({
    mutationFn: async () => (await api.post("/insurance/reminders/send-all")).data,
    onSuccess: (r) => {
      refresh();
      // INS-020: the batch says what was in it. A blocked draft has nowhere to
      // go, and silently coming up short is how "Send all" stops being trusted.
      const held = r.skipped_blocked
        ? ` · ${r.skipped_blocked} need${r.skipped_blocked === 1 ? "s" : ""} an adjuster email`
        : "";
      if (r.failed) toast.error(`Sent ${r.sent} · ${r.failed} failed${held}`);
      else toast.success(`Sent ${r.sent}${held}`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const runNow = useMutation({
    mutationFn: async () => (await api.post("/insurance/reminders/run-now")).data,
    onSuccess: (r) => {
      refresh();
      if (!r.ran) toast.error(r.reason === "disabled" ? "Turn on daily drafting in Settings first" : `Not run (${r.reason})`);
      else toast.success(
        `${r.drafted} drafted${r.blocked ? ` · ${r.blocked} blocked` : ""} · ${r.skipped} skipped · ${r.auto_disabled} closed out`,
      );
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const busy = send.isPending || skip.isPending || sendAll.isPending || runNow.isPending;
  // Send-all skips blocked drafts server-side, so the button must not promise them.
  const sendable = drafts.filter((d) => !d.blocked);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setPreviewId(null); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="reminders-dialog">
        <DialogHeader>
          <DialogTitle>Emails waiting to send</DialogTitle>
          <DialogDescription>Drafted by the CRM — payment chases, and any extension request you asked for. Nothing is emailed until you send it.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground">
            Nothing has been emailed yet. Read one, send it, or skip it — skipping leaves the case alone so it comes back on its next due date.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => runNow.mutate()} disabled={runNow.isPending} data-testid="reminders-run-now-button">
              {runNow.isPending ? "Drafting…" : "Draft today's reminders"}
            </Button>
            <Button
              size="sm" onClick={() => sendAll.mutate()} disabled={busy || sendable.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reminders-send-all-button"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" /> Send all ({sendable.length})
            </Button>
          </div>
        </div>

        {drafts.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="reminders-empty">No reminders waiting.</div>
        ) : (
          <div className="space-y-2.5 pt-2">
            {drafts.map((d) => (
              <div key={d.id} className="rounded-md border border-border bg-card/40 p-3.5" data-testid={`reminder-row-${d.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{d.case_number}</span>
                      {d.kind === "extension" || d.kind === "auth_request" ? (
                        /* INS-019/INS-022: this row is not a payment chase, and
                           "Send all" includes it, so the batch has to say so. */
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-amber-500/15 text-amber-300" data-testid={`reminder-kind-${d.id}`}>
                          {d.kind === "auth_request" ? "Authorization request" : "Extension request"}
                        </span>
                      ) : (
                        <>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${d.script === "A" ? "bg-blue-500/15 text-blue-300" : "bg-purple-500/15 text-purple-300"}`} data-testid={`reminder-script-${d.id}`}>
                            Script {d.script}
                          </span>
                          <span className="text-xs text-muted-foreground">{d.invoice_number}</span>
                          <span className="money text-xs" data-testid={`reminder-balance-${d.id}`}>{money(d.balance)} outstanding</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate" data-testid={`reminder-subject-${d.id}`}>{d.subject}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      To {d.adjuster_name || "the adjuster"} · {d.to_email || "no email on the case"}
                    </div>
                    {d.blocked && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-amber-300" data-testid={`reminder-blocked-${d.id}`}>
                        <WarningCircle size={13} />
                        <span>{d.blocked_reason}</span>
                        <Link
                          to={`/insurance/${d.case_id}`}
                          className="underline underline-offset-2 hover:text-amber-200"
                          data-testid={`reminder-blocked-open-${d.id}`}
                        >
                          Open the case
                        </Link>
                      </div>
                    )}
                    {/* INS-020: a claim or invoice number the case is missing.
                        The draft is sendable — this says so rather than the
                        Send button being taken away. */}
                    {!d.blocked && !!(d.warnings || []).length && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-amber-300/90" data-testid={`reminder-warnings-${d.id}`}>
                        <WarningCircle size={13} />
                        <span>Missing: {d.warnings.join(", ").toLowerCase()}. Sendable anyway.</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setPreviewId(previewId === d.id ? null : d.id)} data-testid={`reminder-preview-${d.id}`}>
                      <Eye size={14} className="mr-1" /> Preview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => skip.mutate(d.id)} disabled={busy} data-testid={`reminder-skip-${d.id}`}>
                      <XCircle size={14} className="mr-1" /> Skip
                    </Button>
                    <Button
                      size="sm" onClick={() => send.mutate(d.id)} disabled={busy || !!d.blocked}
                      title={d.blocked ? d.blocked_reason : undefined}
                      className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`reminder-send-${d.id}`}
                    >
                      <PaperPlaneTilt size={14} className="mr-1" /> Send
                    </Button>
                  </div>
                </div>
                {previewId === d.id && (
                  <div className="mt-3 pt-3 border-t border-border/60" data-testid={`reminder-preview-body-${d.id}`}>
                    <div className="text-xs text-muted-foreground mb-2">Subject: {d.subject}</div>
                    {(preview?.missing_slots || []).length > 0 && (
                      <p className="text-xs text-amber-300/90 mb-2" data-testid={`reminder-missing-slots-${d.id}`}>
                        {preview.missing_slots.map((label) => `${label} missing.`).join(" ")} The email still reads correctly without them.
                      </p>
                    )}
                    <div
                      className="text-sm leading-relaxed [&_p]:mb-3 bg-background/60 rounded p-3"
                      dangerouslySetInnerHTML={{ __html: preview?.body_html || "Loading…" }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REASONS = {
  disabled: "The digest is switched off",
  no_recipient: "Add a recipient email first",
  already_sent_today: "Already sent today",
  nothing_due: "Nothing to report — no approval is expiring in the window",
  email_failed: "Email is not configured on the server (RESEND_API_KEY)",
};
